<script setup>

import MarketoForm from './marketo/form.vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

// Props
const props = defineProps({
  formSettings: {
    type: Object,
    default: () => ({})
  },
  autoOpenOnMount: {
    type: Boolean,
    default: false
  },
  submissionSessionStorageKey: {
    type: String,
    default: ''
  }
})

const successMsgIsVisible = ref(false)

const isPopupOpen = ref(false)
const activePopupFormId = ref('')
const lockedScrollY = ref(0)
const previousHtmlScrollBehavior = ref('')
const previousBodyScrollBehavior = ref('')
const popupFormRef = ref(null)
const popupCloseButtonRef = ref(null)
let restoreFocusEl = null
const hasTriedAutoOpen = ref(false)
const pendingPopupMaxAgeMs = 3000
const marketoSuccessCloseDelayMs = 2000
let marketoSuccessListener = null
let marketoSuccessCloseTimeout = null

const getPopupWindow = () => {
  if (!process.client) return null
  return window
}

const setPendingPopupOpen = (formId = '') => {
  const popupWindow = getPopupWindow()
  if (!popupWindow) return

  const timestamp = Date.now()
  popupWindow.__delightPendingPopupOpen = {
    formId,
    timestamp
  }

  window.setTimeout(() => {
    if (popupWindow.__delightPendingPopupOpen?.timestamp === timestamp) {
      popupWindow.__delightPendingPopupOpen = undefined
    }
  }, pendingPopupMaxAgeMs)
}

const consumePendingPopupOpen = () => {
  const popupWindow = getPopupWindow()
  if (!popupWindow) return null

  const pendingPopup = popupWindow.__delightPendingPopupOpen
  popupWindow.__delightPendingPopupOpen = undefined

  if (!pendingPopup || typeof pendingPopup !== 'object') return null

  const timestamp = Number(pendingPopup.timestamp || 0)
  if (!timestamp || Date.now() - timestamp > pendingPopupMaxAgeMs) return null

  return typeof pendingPopup.formId === 'string' ? pendingPopup.formId : ''
}

const openPopup = (popupFormId = '') => {
  activePopupFormId.value = popupFormId || props.formSettings?.formId || ''
  isPopupOpen.value = true
}

const closePopup = () => {
  isPopupOpen.value = false
  activePopupFormId.value = ''
}

const activeFormSettings = computed(() => {
  const fallbackFormId = props.formSettings?.formId || ''
  const resolvedFormId = activePopupFormId.value || fallbackFormId
  return {
    ...props.formSettings,
    formId: resolvedFormId
  }
})

const getSubmissionSessionStorageKey = () => {
  return props.submissionSessionStorageKey?.trim?.() || ''
}

const hasSubmittedInCurrentSession = () => {
  if (!process.client) return false

  const key = getSubmissionSessionStorageKey()
  if (!key) return false

  try {
    const storedValue = sessionStorage.getItem(key)
    return storedValue === '1' || storedValue === 'true'
  } catch {
    return false
  }
}

const markSubmittedInCurrentSession = () => {
  if (!process.client) return

  const key = getSubmissionSessionStorageKey()
  if (!key) return

  try {
    sessionStorage.setItem(key, 'true')
  } catch {
    // no-op when sessionStorage is unavailable
  }
}

const tryAutoOpen = () => {
  if (!process.client) return
  if (hasTriedAutoOpen.value) return
  if (!props.autoOpenOnMount) return

  const formId = props.formSettings?.formId || ''
  if (!formId) return

  hasTriedAutoOpen.value = true
  if (hasSubmittedInCurrentSession()) return

  activePopupFormId.value = formId
  isPopupOpen.value = true
}

const lockPageScroll = () => {
  if (!process.client) return

  lockedScrollY.value = window.scrollY
  document.body.style.position = 'fixed'
  document.body.style.top = `-${lockedScrollY.value}px`
  document.body.style.left = '0'
  document.body.style.right = '0'
  document.body.style.width = '100%'
  document.body.style.overflow = 'hidden'
}

const unlockPageScroll = () => {
  if (!process.client) return

  previousHtmlScrollBehavior.value = document.documentElement.style.scrollBehavior
  previousBodyScrollBehavior.value = document.body.style.scrollBehavior

  document.documentElement.style.scrollBehavior = 'auto'
  document.body.style.scrollBehavior = 'auto'

  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.width = ''
  document.body.style.overflow = ''

  window.scrollTo({
    top: lockedScrollY.value,
    behavior: 'auto'
  })

  requestAnimationFrame(() => {
    document.documentElement.style.scrollBehavior = previousHtmlScrollBehavior.value
    document.body.style.scrollBehavior = previousBodyScrollBehavior.value
  })
}

const getFocusableElements = (rootEl) => {
  if (!rootEl) return []

  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')

  return Array.from(rootEl.querySelectorAll(selector)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false
    if (el.hidden) return false
    const hasLayout = el.offsetParent !== null || el.getClientRects().length > 0
    return hasLayout
  })
}

const trapFocus = (event) => {
  if (!isPopupOpen.value) return
  if (event.key !== 'Tab') return

  const rootEl = popupFormRef.value
  if (!rootEl) return

  const focusables = getFocusableElements(rootEl)
  if (!focusables.length) {
    event.preventDefault()
    rootEl.focus()
    return
  }

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement

  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
    return
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

const handleOpenPopupClick = (event, buttonEl = null) => {
  if (!process.client) return
  event?.preventDefault?.()
  const selectedPopupButton = buttonEl || event?.currentTarget
  restoreFocusEl = selectedPopupButton instanceof HTMLElement ? selectedPopupButton : document.activeElement
  const popupFormId = selectedPopupButton?.dataset?.popupFormId
  setPendingPopupOpen(popupFormId || '')
  openPopup(popupFormId || '')
}

const handlePopupTriggerClick = (event) => {
  const target = event.target
  if (!(target instanceof Element)) return

  const popupButton = target.closest('.--js-open-popup')
  if (!popupButton) return

  handleOpenPopupClick(event, popupButton)
}

const handlePopupOpenEvent = (event) => {
  const popupFormId = event?.detail?.formId
  setPendingPopupOpen(popupFormId || '')
  openPopup(popupFormId || '')
}

const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    closePopup()
    return
  }

  trapFocus(event)
}

watch(isPopupOpen, (value) => {
  if (value) {
    successMsgIsVisible.value = false
    lockPageScroll()
    requestAnimationFrame(() => {
      const rootEl = popupFormRef.value
      if (!rootEl) return
      rootEl.setAttribute('tabindex', '-1')
      const focusables = getFocusableElements(rootEl)
      ;(focusables[0] || popupCloseButtonRef.value || rootEl).focus?.()
    })
    return
  }

  unlockPageScroll()

  try {
    restoreFocusEl?.focus?.()
  } catch {
    // noop
  }
})

watch(
  () => [props.autoOpenOnMount, props.formSettings?.formId],
  () => {
    tryAutoOpen()
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  document.addEventListener('click', handlePopupTriggerClick, true)
  window.addEventListener('popup:open', handlePopupOpenEvent)

  marketoSuccessListener = (event) => {
    const formId = event?.detail?.formId
    if (!formId) return
    if (formId !== activeFormSettings.value?.formId) return

    markSubmittedInCurrentSession()

    if (marketoSuccessCloseTimeout) {
      window.clearTimeout(marketoSuccessCloseTimeout)
    }

    marketoSuccessCloseTimeout = window.setTimeout(() => {
      marketoSuccessCloseTimeout = null
      closePopup()
      successMsgIsVisible.value = true

      setTimeout(() => {
        successMsgIsVisible.value = false
      }, 5000)
    }, marketoSuccessCloseDelayMs)
  }

  window.addEventListener('marketo:success', marketoSuccessListener)

  const pendingPopupFormId = consumePendingPopupOpen()
  if (pendingPopupFormId !== null) {
    openPopup(pendingPopupFormId)
    return
  }

  tryAutoOpen()
})

onBeforeUnmount(() => {
  if (marketoSuccessCloseTimeout) {
    window.clearTimeout(marketoSuccessCloseTimeout)
  }
  window.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handlePopupTriggerClick, true)
  window.removeEventListener('popup:open', handlePopupOpenEvent)
  if (marketoSuccessListener) {
    window.removeEventListener('marketo:success', marketoSuccessListener)
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<div class="popup-form"
  :class="{ '--popup-open': isPopupOpen }"
  @click.self="closePopup"
  role="presentation">

  <div
    class="popup-form__form"
    ref="popupFormRef"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="activeFormSettings?.heading ? 'popup-form-title' : undefined"
    :aria-label="activeFormSettings?.heading ? undefined : 'Form dialog'">
    <h4 id="popup-form-title" class="popup-form__popup-header">
      {{ activeFormSettings?.heading }}
    </h4>

    <button class="popup-form__popup-close"
      ref="popupCloseButtonRef"
      type="button"
      aria-label="Close dialog"
      @click="closePopup">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M20 4L4 20M4 4L20 20" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <MarketoForm
      v-if="isPopupOpen"
      :key="activeFormSettings.formId"
      :formSettings="activeFormSettings"
    />
  </div>
</div>

<transition name="popup-success">
  <div
    v-if="successMsgIsVisible"
    class="popup-form__success"
  >
    <p class="popup-form__success-text">
      <span class="popup-form__success-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <mask id="mask0_8938_12232" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
            <rect width="16" height="16" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_8938_12232)">
            <path d="M8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1ZM11.8945 5.59082C11.6017 5.29801 11.1269 5.29817 10.834 5.59082L7.12109 9.30371L5.53027 7.71191C5.23743 7.41933 4.76256 7.41931 4.46973 7.71191C4.17683 8.00481 4.17683 8.48054 4.46973 8.77344L6.59082 10.8945C6.88362 11.1873 7.35846 11.1871 7.65137 10.8945L11.8945 6.65137C12.1872 6.35846 12.1873 5.88363 11.8945 5.59082Z" fill="#25BD85"/>
          </g>
        </svg>
      </span>
      <span>Your application has been submitted.</span>
    </p>
  </div>
</transition>

</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.popup-form {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 100px 24px;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.popup-form.--popup-open {
  opacity: 1;
}

.popup-form:not(.--popup-open) {
  pointer-events: none !important;
  opacity: 0 !important;
  z-index: -1 !important;

  & * {
    pointer-events: none !important;
  }

  & :deep(*) {
    pointer-events: none !important;
  }
}

.popup-form__form {
  padding: 24px 12px;
  background-color: var(--color-white);
  border-radius: 12px;
  width: 100%;
  max-width: 572px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  max-height: calc(100dvh - 80px);
  overflow-y: auto;
  transform: translateY(24px);
  opacity: 0;
  transition: transform 0.25s ease, opacity 0.25s ease;

  @media (min-width: 1440px) {
    padding: 32px 32px 24px;
    gap: 32px;
    border-radius: 24px;
  }
}

.popup-form.--popup-open .popup-form__form {
  transform: translateY(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .popup-form,
  .popup-form__form {
    transition: none !important;
  }
}

.popup-form__popup-close {
  position: absolute;
  top: 24px;
  right: 12px;
  border: none;
  background: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;

  @media (min-width: 1440px) {
    top: 32px;
    right: 32px;
  }
}

.popup-form__popup-header {
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: var(--color-gray-900);

  @media (min-width: 1440px) {
    font-size: 28px;
    line-height: 132%; /* 36.96px */
    letter-spacing: -0.56px;
  }
}

.popup-form .popup-form__form.popup-form__form.popup-form__form.popup-form__form {
  & :deep(.marketo-form.marketo-form .mktoLabel.mktoLabel) {
    color: var(--color-gray-350) !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    line-height: 138% !important;
    letter-spacing: -0.26px !important;
    margin-bottom: 6px !important;
  }

  & :deep(.mktoAsterix) {
    display: none !important;
  }

  & :deep(.marketo-form.marketo-form .mktoField:not([type="checkbox"])),
  & :deep(.marketo-form.marketo-form input:not([type="checkbox"])) {
    height: 40px !important;
    letter-spacing: -.28px !important;
    line-height: 19px !important;
  }

  & :deep(.marketo-form .mktoForm .mktoCheckboxList > label),
  & :deep(.marketo-form .mktoForm .mktoCheckboxList label),
  & :deep(.marketo-form .mktoForm input[type="checkbox"] + label),
  & :deep(.marketo-form .mktoForm .mktoCheckboxList label a) {
    color: var(--color-gray-500) !important;
  }

  & :deep(.mktoClear) {
    display: none !important;
  }

  & :deep(.marketo-form .iti__selected-country-primary) {
    border-radius: 8px 0 0 8px;
  }

  & :deep(.marketo-form .iti__selected-country-primary .iti__flag) {
    max-width: 12px;
    max-height: 12px;
  }

  & :deep(.iti__country-list) {
    max-height: 142px !important;
    overflow-y: auto !important;
  }
}

.popup-form__success {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  max-width: 90vw;
}

.popup-form__success-text {
  padding: 6px 15px 7px 15px;
  border-radius: 12px;
  border: 1px solid var(--color-gray-500);
  background: var(--color-gray-900);

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  line-height: 22px; /* 22.4px */
  letter-spacing: -0.32px;
  color: var(--color-white);
}

.popup-form__success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-success-enter-active,
.popup-success-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.popup-success-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.popup-success-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

@media (prefers-reduced-motion: reduce) {
  .popup-success-enter-active,
  .popup-success-leave-active {
    transition: none !important;
  }
}

</style>